import { createSelector } from 'reselect';
import { blocksByTypeSelector } from '../content/selectors';
import { resourceAdditionSelector, getObjects } from '../booking/selectors';
import { RESORT_RESORT, RESORT_HOLIDAYPARK } from './constants';
import { getSubjects, getProperties } from '../../selectors/entitySelectors';
import { getPreference } from '../../selectors/preferenceSelectors';

export const propertyFilterCountSelector = state => state.reservation.filters.properties;
const propertiesInReservationSelector = state => state.entities.accommodationtypes[state.reservation.criteria.resource_id].properties;
export const reservationSelector = state => state.reservation;
const criteriaSelector = state => state.reservation.criteria;
// const filtersSelector = state => state.reservation.filters;
const preferencesSelector = state => state.reservation.criteria.preferences;
const reservedResourcesSelector = state => state.reservation.reserved_resources;
export const resourceInReservationSelector = state => state.entities.accommodationtypes[state.reservation.criteria.resource_id];

/**
 * Get the current resort
 *
 * @param {object} state
 */
export const getResort = (state) => {
  switch (state.reservation.resort_id) {
    case 21: {
      return RESORT_HOLIDAYPARK;
    }
    case 2041: {
      return RESORT_RESORT;
    }
    default: {
      return null;
    }
  }
};

export const getBookingSectionBlocks = createSelector(
  state => state.entities.blocks,
  blocks => Object.values(blocks).filter(block => block.type === 'BookingSection')
);

export const discountSelector = createSelector(
  reservedResourcesSelector,
  reservedResources => reservedResources.reduce((prev, cur) => {
    if (cur.price < 0) {
      return prev + cur.price;
    }
    return prev;
  }, 0)
);

export const getPaidPreferencesSelector = createSelector(
  propertiesInReservationSelector,
  preferences => preferences
    .filter(preference => preference.has_preference_costs || preference.property.preference_costs)
    // Filter duplicates (Hack to fix Newyse issues where resources sometimes receive duplicate properties)
    .filter((item, pos, arr) => {
      const ids = arr.map(i => i.property.id);
      return ids.indexOf(item.property.id) === pos;
    })
);

export const preferencesForReservationSelector = createSelector(
  resourceInReservationSelector,
  resource => ((resource) ? resource.properties : null)
);

export const propertyPreferenceSelector = createSelector(
  preferencesSelector,
  preferences => preferences.filter(p => p.type === 'property')
);

export const getObjectPreferences = createSelector(
  preferencesSelector,
  preferences => preferences.filter(p => p.type === 'object')
);

export const specialInReservationSelector = createSelector(
  reservedResourcesSelector,
  reservedResources => reservedResources.find(r => r.type === 'special')
);

/**
 * Returns an array of subject objects with their selected quantity
 */
export const subjectsInReservationSelector = createSelector(
  getSubjects,
  criteriaSelector,
  (subjects, criteria) => {
    if (!subjects || !Object.keys(subjects).length) return [];
     return criteria.subjects
      .map(s => ({
        ...subjects[s.subject_id],
        quantity: s.quantity
      }))
      .filter(s => parseInt(s.quantity, 10) > 0);
  }
);

/**
 * Returns the total amount of subjects that are billable.
 * Pets and babies are excluded
 */
export const totalBillableSubjects = createSelector(
  subjectsInReservationSelector,
  subjects => subjects.reduce((a, b) => a + ((b.type === 'person' && b.maxAge !== 2) ? parseInt(b.quantity, 10) : 0), 0)
);

// TODO: Resolve issues with recomputing
export const preferencesInReservationSelector = createSelector(
  propertyPreferenceSelector,
  preferencesForReservationSelector,
  (selected, resourcePreferences) => {
    if (resourcePreferences && selected) {
      return selected.map(sp => resourcePreferences.find(rp => rp.property.id === parseInt(sp.id, 10)).property);
    }

    return [];
  }
);

/**
 * Get the categories from the available additions
 */
export const additionCategorySelector = createSelector(
  blocksByTypeSelector('AdditionBlock'),
  resourceAdditionSelector,
  (blocks, additions) => {
    const categories = {};
    blocks.forEach((b) => {
      const availableAdditions = b.resource_additions.filter(a => (additions && Object.keys(additions).indexOf(a.id.toString()) !== -1));
      if (additions) {
        Object.keys(additions).forEach((key) => {
          availableAdditions.forEach((a) => {
            if (a.id.toString() === key) {
              a.price = additions[key].price;
            }
          });
        });
      }

      // If the block has no available additions, we skip to the next block
      if (availableAdditions.length < 1) {
        return;
      }

      // Loop through the blocks categories and map them to the array
      if (b.properties.categories) {
        b.properties.categories.forEach((cat) => {
          categories[cat.key] = cat.value;
        });
      }
    });
    return categories;
  }
);

/**
 * Price line selectors
 */

const isPartOfAllInPrice = (reservedResource) => {
  if (['accommodationtype', 'special'].indexOf(reservedResource.type) !== -1) {
    return true;
  }

  if (['rk', 'tb', 'tbk', 'kep', 'schb', 'skc', 'skb', 'schgb', 'skv'].indexOf(reservedResource.code.toLowerCase()) !== -1) {
    return true;
  }

  return false;
};


const isPartOfAdditionalPrices = (reservedResource) => {
  if (isPartOfAllInPrice(reservedResource)) {
    return false;
  }

  // Avoid rendering
  // - Bedlinnen p.p.
  // - Toiletartikelen
  // - opgemaakte bedding bij aankomst
  if (['bdlpp', 'bdlppservic', 'tp2p', 'obbapp', 'vlvlodgsf6', 'voorldg8p', 'voorgpr14'].indexOf(reservedResource.code.toLowerCase()) !== -1) {
    return false;
  }

  return true;
};

// Defines the sort of the priceline types
const sort = ['accommodationtype', 'special', 'producttype', 'extra'];

export const accoPriceLinesSelector = createSelector(
  reservedResourcesSelector,
  reservedResources => reservedResources.filter(r => r.type === 'accommodationtype')
);

export const specialPriceLinesSelector = createSelector(
  reservedResourcesSelector,
  reservedResources => reservedResources.filter(r => r.type === 'special')
);

export const allInPriceLinesSelector = createSelector(
  reservedResourcesSelector,
  reservedResources => reservedResources.filter(r => isPartOfAllInPrice(r)).sort((a, b) => sort.indexOf(a.type) - sort.indexOf(b.type)).map((r) => {
    let { price } = r;
    if (['voorldg', 'voorldg8p', 'voorgr14'].indexOf(r.code) !== -1) {
      // Combine long stay discounts into one discount line
      price = reservedResources.reduce((prev, cur) => ((['vlvlodgsf6', 'voorldg8p', 'voorgpr14'].indexOf(cur.code) !== -1 && cur.type === 'extra') ? prev + cur.price : prev), r.price);
    }

    return { ...r, price };
  })
);

export const additionPriceLinesSelector = createSelector(
  reservedResourcesSelector,
  reservedResources => reservedResources.filter(r => isPartOfAdditionalPrices(r)).sort((a, b) => sort.indexOf(a.type) - sort.indexOf(b.type))
);

/**
 * Price calculation selectors
 */

// When booking a resort accommodation, Libéma wants to include the local charges and extra persons in the all-in-price
export const localChargesInAllInPriceSelector = createSelector(
  reservationSelector,
  reservedResourcesSelector,
  (reservation, reservedResources) => {
    if (reservation && ((reservation.resort_id === 2041 || reservation.resort_id === 21) && reservedResources.length)) {
      return reservedResources.reduce((total, reservedResource) => {
        if (reservedResource.code === 'lh' || reservedResource.code === 'extra') {
          total += reservedResource.price * reservedResource.quantity;
        }
        return total;
      }, 0);
    }
    return 0;
  }
);

export const extraPersonsSubtotalSelector = createSelector(
  reservationSelector,
  reservedResourcesSelector,
  (reservation, reservedResources) => {
    if (reservation && ((reservation.resort_id === 2041 || reservation.resort_id === 21) && reservedResources.length)) {
      return reservedResources.reduce((total, reservedResource) => {
        if (reservedResource.code === 'extra') {
          total += reservedResource.price * reservedResource.quantity;
        }
        return total;
      }, 0);
    }
    return 0;
  }
);

export const pricePerPersonSelector = createSelector(
  reservationSelector,
  subjectsInReservationSelector,
  (reservation, subjects) => Math.round(((reservation.total_price / (subjects.reduce((a, b) => a + parseInt(b.quantity, 10), 0))) * 100)) / 100
);

const extraPersonChargesSelector = createSelector(
  reservedResourcesSelector,
  (reservedResources) => {
    const reservedResource = reservedResources.find(r => r.code.startsWith('extra'));
    if (!reservedResource) {
      return 0;
    }

    return reservedResource.price * reservedResource.quantity;
  }
);

export const allInPriceSelector = createSelector(
  allInPriceLinesSelector,
  localChargesInAllInPriceSelector,
  extraPersonChargesSelector,
  (allInPriceLines, localCharges, extraPersonCharges) => ((allInPriceLines.length) ? allInPriceLines.reduce((a, b) => a + (b.price * b.quantity), localCharges + extraPersonCharges) : 0)
);

export const subtotalSelector = createSelector(
  allInPriceLinesSelector,
  extraPersonsSubtotalSelector,
  (reservedResources, extraPersons) => ((reservedResources.length) ? reservedResources.filter(r => r.type === 'accommodationtype').reduce((a, b) => a + (b.price * b.quantity), extraPersons) : 0)
);


/**
 * Get the object preference in the current reservation.
 *
 * Returns either null or an Object object.
 */
export const getObjectPreferenceInReservation = createSelector(
  getObjects,
  getObjectPreferences,
  (objects, objectPreferences) => {
    if (!objects || !objectPreferences) {
      return null;
    }

    return objectPreferences.map(op => objects[op.id]).find(() => true);
  }
);


/**
 * Get the available objects for the current reservation
 */
export const getAvailableObjectsForReservation = createSelector(
  getObjects,
  reservationSelector,
  (allObjects, reservation) => {
    if (Object.keys(allObjects).length > 0 && reservation.objects.length > 0) {
      return reservation.objects.map(o => allObjects[o]);
    }

    return [];
  }
);

export const getPropertiesWithPreferenceCost = createSelector(
  getProperties,
  properties => Object.values(properties).reduce((acc, property) => {
    if (property.preference_costs) {
      acc.push(property.id);
    }
    return acc;
  }, [])
);

export const selectedPropertiesWithPreferenceCost = createSelector(
  getPropertiesWithPreferenceCost,
  getPreference('properties'),
  (properties, selectedProps) => selectedProps.filter(prop => properties.indexOf(prop) > -1)
);
